import React, { useEffect, useState, useMemo, useRef } from "react";
import { toCommas } from "../../utils/utils";
import styles from "./Dashboard.module.css";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableContainer,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Grid,
  Box,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { useHistory, useLocation, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getInvoicesByUser, getInvoices, getInvoicesByPage } from "../../actions/invoiceActions";
import { getUsers } from "../../actions/auth";
import { getMeetings } from "../../actions/meetingActions";
import { getAllClients, getInactiveClients } from "../../actions/clientActions";
import Empty from "../svgIcons/Empty";
import Chart from "./Chart";
import moment from "moment";
import {
  Check,
  Pie,
  Bag,
  Card as CardIcon,
  Clock,
  Frown,
  Order,
  Rupee,
  Report,
  SalesPerson,
  Item,
} from "./Icons";
import Spinner from "../Spinner/Spinner";

const Dashboard = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("profile"));
  const { users } = useSelector((state) => state.auth);
  const [dueDays, setDueDays] = useState(5);
  const [overdueClientsResult, setOverdueClientsResult] = useState([]);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [searchAgentCode, setSearchAgentCode] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [isLoadingInactive, setIsLoadingInactive] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [userRequested, setUserRequested] = useState(false);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  
  // Add refs to prevent multiple API calls
  const hasAutoLoaded = useRef(false);
  const isInitialMount = useRef(true);

  const clients = useSelector((state) => state.clients.clients);
  const inactiveClients = useSelector((state) => state.clients.inactiveClients);
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState([]);
  const invoices = useSelector((state) => state?.invoices.invoices);
  const isLoading = useSelector((state) => state.invoices.isLoading);
  const { meetings } = useSelector((state) => state?.meetings);

  // Fetch invoices only once on mount
  useEffect(() => {
    if (isInitialMount.current) {
      dispatch(getInvoices());
      isInitialMount.current = false;
    }
  }, []);

  useEffect(() => {
    if (invoices && invoices.length > 0) {
      const today = moment().format("YYYY-MM-DD");
      const todayRows = invoices.filter((inv) =>
        moment(inv.date).isSame(today, "day")
      );
      if (todayRows.length > 0) {
        setRows(todayRows);
      } else {
        setRows([]);
      }
      setIsInitialDataLoaded(true);
    }
  }, [invoices]);

  // Fetch clients only once on mount
  useEffect(() => {
    if (isInitialMount.current) {
      dispatch(getAllClients());
    }
  }, []);

  // Auto-load inactive clients only once when clients are available
  useEffect(() => {
    if (clients.length > 0 && !hasAutoLoaded.current && !isAutoLoading) {
      hasAutoLoaded.current = true;
      setIsAutoLoading(true);
      
      dispatch(getInactiveClients(5))
        .then(() => {
          setIsAutoLoading(false);
        })
        .catch((error) => {
          console.error("Error in auto-load:", error);
          setIsAutoLoading(false);
          // Reset flag on error so user can retry
          hasAutoLoaded.current = false;
        });
    }
  }, [clients, dispatch]);

  useEffect(() => {
    if (clients.length > 0 && rows.length > 0) {
      const today = moment();
      const overdueClients = clients.filter((client) => {
        const clientInvoices = rows.filter(
          (invoice) => invoice.client._id === client._id
        );
        if (clientInvoices.length === 0) {
          return true;
        }
        const lastInvoiceDate = moment.max(
          clientInvoices.map((invoice) => moment(invoice.date))
        );
        const dueDate = moment(today).subtract(5, "days");
        return lastInvoiceDate.isBefore(dueDate, "day");
      });
      setOverdueClientsResult(overdueClients);
      setFilteredResults(overdueClients);
    }
  }, [clients, rows]);

  const overDue = rows?.filter(
    (invoice) => invoice.dueDate <= new Date().toISOString()
  );
  const admins = users.filter((user) => user.userType === "admin");
  const regularUsers = users.filter((user) => user.userType === "user");
  const activeUsers = regularUsers.filter((user) => user.isActive === true);
  let paymentHistory = [];
  for (let i = 0; i < rows.length; i++) {
    let history = [];
    if (rows[i].paymentRecords !== undefined) {
      history = [...paymentHistory, rows[i].paymentRecords];
      paymentHistory = [].concat.apply([], history);
    }
  }
  const sortHistoryByDate = paymentHistory.sort(function (a, b) {
    var c = new Date(a.datePaid);
    var d = new Date(b.datePaid);
    return d - c;
  });
  let totalPaid = 0;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].totalAmountReceived !== undefined) {
      totalPaid += rows[i].totalAmountReceived;
    }
  }
  let totalAmount = 0;
  for (let i = 0; i < rows.length; i++) {
    totalAmount += rows[i].total;
  }
  const handleDueDaysChange = (event) => {
    const newValue = event.target.value;
    setDueDays(newValue);
    if (newValue !== "5") {
      setOverdueClientsResult([]);
      setFilteredResults([]);
      setSearchAgentCode("");
      setUserRequested(false);
    }
  };
  const handleSearchAgentCode = (event) => {
    const searchValue = event.target.value;
    setSearchAgentCode(searchValue);
    if (searchValue.trim() === "") {
      setFilteredResults(overdueClientsResult);
    } else {
      const filtered = overdueClientsResult.filter((client) =>
        client.agentCode.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredResults(filtered);
    }
  };
  const clearSearch = () => {
    setSearchAgentCode("");
    setFilteredResults(overdueClientsResult);
  };
  const handleSubmit = async () => {
    if (!dueDays || dueDays <= 0) {
      alert("Please enter a valid number of days");
      return;
    }
    setIsLoadingInactive(true);
    try {
      await dispatch(getInactiveClients(dueDays));
      setLastUpdated(new Date());
      setUserRequested(true);
      setIsLoadingInactive(false);
    } catch (error) {
      console.error("Error fetching inactive clients:", error);
      setIsLoadingInactive(false);
    }
  };
  useEffect(() => {
    if (inactiveClients && inactiveClients.length >= 0) {
      setOverdueClientsResult(inactiveClients);
      setFilteredResults(inactiveClients);
      if (!userRequested) {
        setLastUpdated(new Date());
      }
    }
  }, [inactiveClients, userRequested]);
  const paid = rows?.filter((invoice) => invoice.status === "Paid");
  const partial = rows?.filter((invoice) => invoice.status === "Partial");

  if (!user) {
    history.push("/login");
  }

  if (!isInitialDataLoaded && isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
        <p className={styles.loadingText}>Loading dashboard data...</p>
      </div>
    );
  }

  console.log("Current state:", {
    isLoading,
    invoicesLength: invoices?.length || 0,
    rowsLength: rows?.length || 0,
    isInitialDataLoaded,
    hasAutoLoaded: hasAutoLoaded.current,
    isAutoLoading,
  });

  return (
    <div className={styles.pageContainer}>


      {/* Main Content Grid */}
      <div className={styles.mainGrid}>
        {/* Today's Bookings Card */}
        <div className={styles.bookingsCard}>
          <Link to="/todayBookings" className={styles.link}>
            <Card className={styles.enhancedCard}>
              <CardContent className={styles.cardContent}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardInfo}>
                    <Typography variant="h2" className={styles.cardNumber}>
                      {rows.length}
                    </Typography>
                    <Typography variant="h5" className={styles.cardTitle}>
                      Today's Bookings
                    </Typography>
                    <Typography variant="body2" className={styles.cardDate}>
                      {moment().format("Do MMM YYYY")}
                    </Typography>
                    {rows.length === 0 && (
                      <Typography variant="body2" className={styles.cardSubtext}>
                        No bookings today
                      </Typography>
                    )}
                  </div>
                  <div className={styles.cardIcon}>
                    <Order />
                  </div>
                </div>
                <Divider className={styles.cardDivider} />
             
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Inactive Clients Analysis Card */}
        <div className={styles.analysisCard}>
          <Card className={styles.enhancedCard}>
            <CardContent className={styles.cardContent}>
                         
                <div className={styles.inputSection}>
                  <div className={styles.inputGroup}>
                    <Typography variant="body1" className={styles.label}>
                      Zero Booking in past{" "}
                      <input
                        id="dueDays"
                        type="number"
                        value={dueDays}
                        onChange={handleDueDaysChange}
                        className={styles.inputField}
                        min="1"
                        max="365"
                      />{" "}
                      Days
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={isLoadingInactive || isAutoLoading}
                      className={styles.goButton}
                      size="medium"
                    >
                      {isLoadingInactive ? "Loading..." : "Go!"}
                    </Button>
                  </div>
                  
         

                  {/* Show loading state when auto-loading */}
                  {isAutoLoading && !userRequested && (
                    <div className={styles.loading}>
                      <div className={styles.spinner}></div>
                      <Typography variant="body2" className={styles.loadingText}>
                        Fetching inactive client data...
                      </Typography>
                    </div>
                  )}

                  {/* Results Summary - Only show after auto-load completes */}
                  {!isAutoLoading && overdueClientsResult && overdueClientsResult.length > 0 && (
                    <div className={styles.summary}>
                      <Typography variant="body1" className={styles.summaryText}>
                        📊 Found {overdueClientsResult.length} inactive client
                        {overdueClientsResult.length !== 1 ? "s" : ""} with 0 bookings
                        in the last {dueDays} days
                      </Typography>
                      <Typography variant="caption" className={styles.summarySubtext}>
                        Includes clients with no recent activity and new clients with
                        no bookings
                      </Typography>
                      <Typography variant="caption" className={styles.summarySubtext}>
                        Showing data for past {dueDays} days from today
                        {!userRequested && " (auto-loaded)"}
                      </Typography>
                    </div>
                  )}

                  {/* Loading State for manual search */}
                  {isLoadingInactive && (
                    <div className={styles.loading}>
                      <div className={styles.spinner}></div>
                      <Typography variant="body2" className={styles.loadingText}>
                        Finding inactive clients...
                      </Typography>
                    </div>
                  )}

                  {/* Search Section - Only show after auto-load completes */}
                  {!isAutoLoading && overdueClientsResult && overdueClientsResult.length > 0 && (
                    <div className={styles.searchContainer}>
                      <div className={styles.searchInput}>
                        <input
                          type="text"
                          placeholder="Enter agent code to search..."
                          value={searchAgentCode}
                          onChange={handleSearchAgentCode}
                          className={styles.searchField}
                        />
                        {searchAgentCode && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={clearSearch}
                            className={styles.clearButton}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      {searchAgentCode && (
                        <Typography variant="caption" className={styles.searchResult}>
                          Found {filteredResults.length} result
                          {filteredResults.length !== 1 ? "s" : ""}
                        </Typography>
                      )}
                    </div>
                  )}

                  {/* Info Messages - Only show after auto-load completes */}
                  {!isAutoLoading && dueDays !== 5 && overdueClientsResult.length === 0 && !isLoadingInactive && (
                    <div className={styles.infoBox}>
                      <Typography variant="body2" className={styles.infoText}>
                        📝 You've changed the days to {dueDays}. Click "Go!" to search
                        for inactive clients.
                      </Typography>
                    </div>
                  )}

                  {!isAutoLoading && !isLoadingInactive &&
                    overdueClientsResult &&
                    overdueClientsResult.length === 0 &&
                    userRequested && (
                      <div className={styles.successBox}>
                        <Typography variant="body2" className={styles.successText}>
                          🎉 Great! All clients have been active in the last {dueDays}{" "}
                          days.
                        </Typography>
                        <Typography
                          variant="caption"
                          className={styles.successSubtext}
                        >
                          No inactive clients found for the past {dueDays} days
                        </Typography>
                      </div>
                    )}

                  {/* Results Table - Only show after auto-load completes */}
                  {!isAutoLoading && overdueClientsResult && overdueClientsResult.length > 0 && (
                    <div className={styles.tableContainer}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell className={styles.tableHeader}>Name</TableCell>
                              <TableCell className={styles.tableHeader}>Code</TableCell>
                              <TableCell className={styles.tableHeader}>Last Invoice</TableCell>
                              <TableCell className={styles.tableHeader}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(searchAgentCode ? filteredResults : overdueClientsResult).map(
                              (client) => (
                                <TableRow key={client._id} className={styles.tableRow}>
                                  <TableCell className={styles.tableCell}>
                                    <Typography variant="body2" className={styles.clientName}>
                                      {client.name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell className={styles.tableCell}>
                                    <Chip 
                                      label={client.agentCode} 
                                      size="small" 
                                      className={styles.agentCodeChip}
                                    />
                                  </TableCell>
                                  <TableCell className={styles.tableCell}>
                                    <Typography variant="body2">
                                      {client.lastInvoiceDate
                                        ? moment(client.lastInvoiceDate).format(
                                            "MMM DD, YYYY"
                                          )
                                        : "Never"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell className={styles.tableCell}>
                                    <Chip 
                                      label={
                                        client.reason === "never_had_invoices"
                                          ? "New Client"
                                          : client.reason === "no_recent_activity"
                                          ? "Inactive"
                                          : "Unknown"
                                      }
                                      size="small"
                                      color={
                                        client.reason === "never_had_invoices"
                                          ? "primary"
                                          : client.reason === "no_recent_activity"
                                          ? "secondary"
                                          : "default"
                                      }
                                      className={styles.statusChip}
                                    />
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                            {searchAgentCode && filteredResults.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} className={styles.noResultsCell}>
                                  <Typography variant="body1" color="textSecondary">
                                    No clients found with agent code "{searchAgentCode}"
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  )}
                </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;